<template>
  <component
    :is="modalComponent"
    v-if="visible"
    :show="visible"
    :icon="icon"
    :title="title"
    :show-close-button="showCloseButton"
    :close-on-escape="closeOnEscape"
    :type="type"
    :actions="actions"
    :text-align="textAlign"
    :clickaway="clickaway"
    :role="role"
    :aria-label="ariaLabel"
    :header-style="headerStyle"
    :size="size"
    :allow-overflow="allowOverflow"
    @close-modal="handleCloseModal"
  >
    <template v-if="!preset && (headerComponent || title)" #header>
      <component
        :is="headerComponent"
        v-if="headerComponent"
        v-bind="headerProps"
      />
      <template v-else-if="!!title">
        <MHeading tag="h2" size="h4">{{ title }}</MHeading>
      </template>
    </template>

    <component :is="bodyComponent" v-if="bodyComponent" v-bind="bodyProps" />
    <template v-else-if="!!text">{{ text }}</template>

    <template #footer>
      <component
        :is="footerComponent"
        v-if="footerComponent"
        v-bind="footerProps"
      />
      <template v-else-if="!!footer">{{ footer }}</template>
    </template>
  </component>
</template>

<script>
import { computed, ref, watch } from 'vue'

import useModal from '../../../composables/useModal'
import MHeading from '../../typography/Heading/MHeading'

import MModalAlert from './Alert/MModalAlert.ts.vue'
import MModalDialog from './Dialog/MModalDialog.ts.vue'
import MModal from './MModal'
import MModalTooltip from './Tooltip/MModalTooltip.ts.vue'

/**
 * A component that pairs with and provides modal plug-in support.
 *
 * See README for more information regarding the modal plugin.
 * https://patterns.many.engineering/?path=/docs/m-docs-getting-started--page
 */
export default {
  name: 'MModalContainer',
  components: {
    MHeading,
    MModal,
    MModalAlert,
    MModalDialog,
    MModalTooltip,
  },
  setup() {
    const { modalState, dismiss: modalDismiss } = useModal()
    //#region Init refs
    const visible = ref(false)
    const title = ref(null)
    const text = ref(null)
    const footer = ref(null)
    const headerComponent = ref(null)
    const headerProps = ref(null)
    const headerStyle = ref(null)
    const bodyComponent = ref(null)
    const bodyProps = ref(null)
    const footerComponent = ref(null)
    const footerProps = ref(null)
    const showCloseButton = ref(true)
    const closeOnEscape = ref(true)
    const clickaway = ref(true)
    const role = ref(null)
    const ariaLabel = ref('')
    const routeDismiss = ref(true)
    const size = ref('medium')
    const icon = ref('')
    const type = ref('info')
    const preset = ref('modal')
    const textAlign = ref('left')
    const allowOverflow = ref(false)
    const actions = ref(null)
    //#endregion

    //#region Control functions
    const dismiss = () => {
      visible.value = false
    }

    const show = (params) => {
      // making modal visible
      visible.value = true
      // setting title and text
      title.value = params.title
      text.value = params.text
      footer.value = params.footer
      headerComponent.value = params.headerComponent
      headerProps.value = params.headerProps
      headerStyle.value = params.headerStyle
      bodyComponent.value = params.bodyComponent
      bodyProps.value = params.bodyProps
      footerComponent.value = params.footerComponent
      footerProps.value = params.footerProps
      role.value = params.role
      ariaLabel.value = params.ariaLabel
      routeDismiss.value = params.routeDismiss
      size.value = params.size
      icon.value = params.icon
      type.value = params.type
      textAlign.value = params.textAlign
      allowOverflow.value = params.allowOverflow
      preset.value = params.preset
      actions.value = params.actions

      // Make sure these stay defaulted as true
      if (Object.prototype.hasOwnProperty.call(params, 'showCloseButton')) {
        showCloseButton.value = params.showCloseButton
      }

      if (Object.prototype.hasOwnProperty.call(params, 'clickaway')) {
        clickaway.value = params.clickaway
      }

      if (Object.prototype.hasOwnProperty.call(params, 'closeOnEscape')) {
        closeOnEscape.value = params.closeOnEscape
      }
    }
    //#endregion

    //#region Events
    const handleCloseModal = (args) => {
      // We go through the official channel here rather than just setting
      // `this.visible` to `false`) so that all "cleanup" runs (i.e. the "show"
      // promise resolves, but maybe other stuff in the future)
      modalDismiss(args)
    }

    watch(modalState, (value) => {
      if (value) {
        show(value)
      } else {
        dismiss()
      }
    })

    //#endregion

    //#region Modal component
    const modalComponent = computed(() => {
      const presets = {
        alert: 'MModalAlert',
        dialog: 'MModalDialog',
        tooltip: 'MModalTooltip',
      }

      return presets[preset.value] || 'MModal'
    })
    //#endregion

    return {
      modalState,
      visible,
      title,
      text,
      footer,
      headerComponent,
      headerProps,
      headerStyle,
      bodyComponent,
      bodyProps,
      footerComponent,
      footerProps,
      showCloseButton,
      clickaway,
      role,
      ariaLabel,
      routeDismiss,
      size,
      icon,
      type,
      preset,
      allowOverflow,
      textAlign,
      actions,
      handleCloseModal,
      show,
      dismiss,
      modalComponent,
      closeOnEscape,
    }
  },
}
</script>
